// src/components/referrals/ReferralSummaryCard.jsx //
import React from "react";
import Card from "@/components/ui/Card.jsx";
import SignatureStampLine from "@/components/prescriptions/SignatureStampLine.jsx";
import { useLanguage } from "@/contexts/LanguageContext.jsx";
import {
  selectDoctorLicenseNumber,
  selectDoctorPrescriptionName,
} from "@/utils/prescriptionDoctorIdentity.js";
import {
  CLINICIAN_SIGNATURE_PRESENTATION,
  DOCTOR_STAMP_PRESENTATION,
} from "@/components/prescriptions/doctorStampPresentation.js";

const REFERRAL_SIGNATURE_PLACEHOLDER = "/icons/appointments/e-prescription/Signature.svg";
const REFERRAL_STAMP_BASE = "/icons/appointments/e-prescription/afrimed-dr-stamp-base-flat.png";

function safeText(value, fallback = "—") {
  const text = String(value || "").trim();
  return text || fallback;
}

function formatLongDate(value) {
  const date = value ? new Date(value) : null;
  if (!date || Number.isNaN(date.getTime())) return "—";

  return new Intl.DateTimeFormat("en-GB", {
    day: "2-digit",
    month: "long",
    year: "numeric",
  }).format(date);
}

function formatCreatedAt(value) {
  const date = value ? new Date(value) : null;
  if (!date || Number.isNaN(date.getTime())) return "—";

  return new Intl.DateTimeFormat("en-GB", {
    day: "2-digit",
    month: "long",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  }).format(date);
}

function formatDob(value) {
  const date = value ? new Date(value) : null;
  if (!date || Number.isNaN(date.getTime())) return "";

  return new Intl.DateTimeFormat("en-GB", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
  }).format(date);
}

function formatDoctorLabel(value) {
  const text = String(value || "")
    .replace(/^dr\.?\s+/i, "")
    .trim();
  if (!text) return "Doctor";
  const parts = text.split(/\s+/).filter(Boolean);
  const surname = parts[parts.length - 1] || text;
  return `Dr ${surname}`;
}

function formatReferralHeading(urgency) {
  return String(urgency || "").trim().toLowerCase() === "urgent"
    ? "Urgent referral"
    : "Referral";
}

function formatStatus(value) {
  const text = String(value || "").trim().toLowerCase();
  if (!text) return "—";
  return text.charAt(0).toUpperCase() + text.slice(1);
}

function formatUrgency(value) {
  const text = String(value || "").trim().toLowerCase();
  if (!text) return "—";
  if (text === "soon") return "Soon";
  if (text === "urgent") return "Urgent";
  return "Routine";
}

function formatRecipientLine(name, specialty) {
  const recipient = String(name || "").trim();
  const governedSpecialty = String(specialty || "").trim();
  if (!recipient) return "";
  if (!governedSpecialty) return recipient;
  const specialistLabel = /\bspecialist\b/i.test(governedSpecialty)
    ? governedSpecialty
    : `${governedSpecialty} Specialist`;
  return `${recipient} • ${specialistLabel}`;
}

function formatParagraph(value, fallback = "—") {
  const text = String(value || "").trim();
  if (!text) return fallback;
  return text;
}

function Field({ label, children }) {
  return (
    <div className="space-y-1">
      <div className="text-label-medium text-on-surface-var">{label}</div>
      <div className="text-body-medium text-on-surface whitespace-pre-line">{children}</div>
    </div>
  );
}

export default function ReferralSummaryCard({ referral, documentContext = null, printRef = null }) {
  const { t, formatDate } = useLanguage();
  if (!referral) return null;

  const toLine =
    formatRecipientLine(
      documentContext?.acceptedDoctorName,
      documentContext?.acceptedDoctorSpecialty,
    ) ||
    referral?.note_text ||
    (referral?.to_specialty_label ? `${referral.to_specialty_label} specialist` : "Specialist");

  const patientName = safeText(documentContext?.patientName, "");
  const patientDob = formatDob(documentContext?.patientDob);
  const patientLine = patientName
    ? `${patientName}${patientDob ? ` (DOB: ${patientDob})` : ""}`
    : "—";

  const doctorLabel = formatDoctorLabel(documentContext?.doctorName);
  const signatureUrl = String(documentContext?.doctorSignatureUrl || "").trim();
  const practiceLogoUrl = String(documentContext?.practiceLogoUrl || "").trim();
  const facilityAddressLines = Array.isArray(documentContext?.facilityAddressLines)
    ? documentContext.facilityAddressLines.map((line) => safeText(line)).filter(Boolean)
    : [safeText(documentContext?.facilityAddress)].filter(Boolean);
  const clinicianIdentity = {
    doctorName: documentContext?.doctorName,
    registerNumber: documentContext?.doctorRegistrationNumber,
  };
  const clinicianName = selectDoctorPrescriptionName(clinicianIdentity) || doctorLabel;
  const clinicianRegistration = selectDoctorLicenseNumber(clinicianIdentity);
  const isReceivingClinician = documentContext?.viewerRole === "receiving_clinician";
  const screenSubtitle = isReceivingClinician
    ? t("referrals.summary.recipientSubtitle", "Referral issued by {doctorName}.", { doctorName: clinicianName })
    : t("referrals.summary.subtitle", "Your referral document issued by your doctor.");

  return (
    <>
      <Card className="p-4 space-y-4">
        {practiceLogoUrl ? (
          <div className="flex items-start justify-between gap-4">
            <img
              src={practiceLogoUrl}
              alt="Practice logo"
              className="max-h-12 max-w-[120px] object-contain"
            />
          </div>
        ) : null}

        <div className="space-y-1">
          <div className="text-title-medium text-on-surface">
            {formatReferralHeading(referral.urgency)}
          </div>
          <div className="text-body-medium text-on-surface-var">
            {screenSubtitle}
          </div>
        </div>

        <div className="space-y-1 text-body-medium text-on-surface">
          <div><span className="text-label-large text-on-surface">{t("referrals.summary.date", "Date")}:</span>{" "}{formatDate(referral.created_at, { day: "2-digit", month: "long", year: "numeric" }) || formatLongDate(referral.created_at)}</div>
          <div><span className="text-label-large text-on-surface">{t("referrals.summary.to", "To")}:</span>{" "}{safeText(toLine)}</div>
          <div><span className="text-label-large text-on-surface">{t("referrals.summary.from", "From")}:</span>{" "}{doctorLabel}</div>
          <div><span className="text-label-large text-on-surface">{t("referrals.summary.patient", "Patient")}:</span>{" "}{patientLine}</div>
        </div>

        <div className="h-px bg-[rgba(var(--clr-outline-variant),0.9)]" />

        <div className="space-y-4">
          <Field label={t("referrals.summary.reason", "Reason for referral")}>{formatParagraph(referral.reason || referral.clinical_history)}</Field>
          <Field label={t("referrals.summary.clinicalHistory", "Clinical history & findings")}>{formatParagraph(referral.clinical_history || referral.reason)}</Field>
          <Field label={t("referrals.summary.specificRequest", "Specific request")}>{formatParagraph(referral.specific_request)}</Field>
          <Field label={t("referrals.summary.urgency", "Urgency")}>{t(`referrals.form.urgency.${String(referral.urgency || "").toLowerCase()}`, formatUrgency(referral.urgency))}</Field>
          <Field label={t("referrals.summary.status", "Status")}>{formatStatus(referral.status)}</Field>
          <Field label={t("referrals.summary.created", "Created")}>{formatCreatedAt(referral.created_at)}</Field>
        </div>
      </Card>

      <section className="space-y-3 px-1 pt-1">
        <div>
          <div className="text-title-medium text-on-surface">{t("referrals.summary.signature", "Physician signature")}</div>
          <div className="mt-1 text-body-small text-on-surface-var">
            {t("referrals.summary.signatureIssuedBy", "Issued by {doctorLabel}.", { doctorLabel: clinicianName })}
          </div>
          {clinicianRegistration ? <div className="mt-1 text-body-small text-on-surface-var">{t("referrals.summary.registration", "Registration")}: {clinicianRegistration}</div> : null}
        </div>
        <div className="-mt-1">
          <SignatureStampLine
            sigSrc={signatureUrl || REFERRAL_SIGNATURE_PLACEHOLDER}
            stampBaseSrc={REFERRAL_STAMP_BASE}
            doctorName={clinicianName}
            regNo={clinicianRegistration}
            stampSizePx={DOCTOR_STAMP_PRESENTATION.sizePx}
            sigClassName="h-[68px] w-[160px]"
            sigNudgeY={CLINICIAN_SIGNATURE_PRESENTATION.nudgeYPx}
            stampNudgeY={DOCTOR_STAMP_PRESENTATION.nudgeYPx}
          />
        </div>
        <div className="text-body-small text-on-surface-var">
          {t("referrals.summary.clinicalResponsibility", "Powered by AfriMed™. The referring clinician remains responsible for clinical decisions.")}
        </div>
      </section>

      {/* This is the same print-only document boundary used by Prescription. */}
      <div className="print-only">
        <div
          ref={printRef}
          className="print-paper print-reset-spacing h-auto mx-auto w-full max-w-[720px] px-4 pt-6 pb-6"
          style={{ fontFamily: "Inter, system-ui, -apple-system, Segoe UI, Roboto, Helvetica, Arial" }}
        >
          <div className="grid grid-cols-[112px_minmax(0,1fr)] items-start gap-6">
            {practiceLogoUrl ? (
              <img
                src={practiceLogoUrl}
                alt="Practice logo"
                className="h-auto w-[112px] object-contain"
              />
            ) : null}
            <div className="col-start-2 min-w-0 text-right">
              {documentContext?.facilityName ? <div className="text-title-medium text-on-surface">{documentContext.facilityName}</div> : null}
              {facilityAddressLines.map((line, index) => (
                <div key={`${line}-${index}`} className={`${index === 0 ? "mt-1 " : ""}text-body-small text-on-surface-var`}>
                  {line}
                </div>
              ))}
            </div>
          </div>

          <div className="mt-5 space-y-1">
            <div className="text-title-large text-on-surface">{formatReferralHeading(referral.urgency)}</div>
            <div className="text-body-medium text-on-surface-var">
              {t("referrals.summary.subtitle", "Your referral document issued by your doctor.")}
            </div>
          </div>

          <div className="mt-3 space-y-1 text-body-medium text-on-surface">
            <div><span className="text-label-large">{t("referrals.summary.date", "Date")}:</span>{" "}{formatDate(referral.created_at, { day: "2-digit", month: "long", year: "numeric" }) || formatLongDate(referral.created_at)}</div>
            <div><span className="text-label-large">{t("referrals.summary.to", "To")}:</span>{" "}{safeText(toLine)}</div>
            <div><span className="text-label-large">{t("referrals.summary.from", "From")}:</span>{" "}{doctorLabel}</div>
            <div><span className="text-label-large">{t("referrals.summary.patient", "Patient")}:</span>{" "}{patientLine}</div>
          </div>

          <div className="my-5 h-px bg-[rgba(var(--clr-outline-variant),0.9)]" />

          <div className="space-y-4">
            <Field label={t("referrals.summary.reason", "Reason for referral")}>{formatParagraph(referral.reason || referral.clinical_history)}</Field>
            <Field label={t("referrals.summary.clinicalHistory", "Clinical history & findings")}>{formatParagraph(referral.clinical_history || referral.reason)}</Field>
            <Field label={t("referrals.summary.specificRequest", "Specific request")}>{formatParagraph(referral.specific_request)}</Field>
            <Field label={t("referrals.summary.urgency", "Urgency")}>{t(`referrals.form.urgency.${String(referral.urgency || "").toLowerCase()}`, formatUrgency(referral.urgency))}</Field>
          </div>

          <div className="my-5 h-px bg-[rgba(var(--clr-outline-variant),0.9)]" />
          <div className="text-title-medium text-on-surface">{t("referrals.summary.signature", "Physician signature")}</div>
          <div className="mt-1 text-body-small text-on-surface-var">
            {t("referrals.summary.signatureIssuedBy", "Issued by {doctorLabel}.", { doctorLabel: clinicianName })}
          </div>
          {clinicianRegistration ? <div className="mt-1 text-body-small text-on-surface-var">{t("referrals.summary.registration", "Registration")}: {clinicianRegistration}</div> : null}
          <div className="mt-3">
            <SignatureStampLine
              sigSrc={signatureUrl || REFERRAL_SIGNATURE_PLACEHOLDER}
              stampBaseSrc={REFERRAL_STAMP_BASE}
              doctorName={clinicianName}
              regNo={clinicianRegistration}
              stampSizePx={DOCTOR_STAMP_PRESENTATION.sizePx}
              sigClassName="h-[68px] w-[160px]"
              sigNudgeY={CLINICIAN_SIGNATURE_PRESENTATION.nudgeYPx}
              stampNudgeY={DOCTOR_STAMP_PRESENTATION.nudgeYPx}
            />
          </div>
          <div className="mt-3 text-body-small text-on-surface-var">
            {t("referrals.summary.clinicalResponsibility", "Powered by AfriMed™. The referring clinician remains responsible for clinical decisions.")}
          </div>
        </div>
      </div>
    </>
  );
}
